import type { ScheduleSummary } from '@dyspatch/core';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { CalendarClock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useSession } from '@/components/auth/session';
import {
  Age,
  EmptyState,
  LoadError,
  RepoChip,
  RowSkeleton,
  TaskKey,
} from '@/components/common/bits';
import { SortMenu } from '@/components/common/SortMenu';
import { Page, SplitPane, ViewHeader } from '@/components/layout/Page';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { SchedulePausedChip } from '@/features/schedules/ScheduleActions';
import { ScheduleComposer } from '@/features/schedules/ScheduleComposer';
import { ScheduleDetail } from '@/features/schedules/ScheduleDetail';
import { useSchedules } from '@/hooks/use-fleet';
import { useWideLayout } from '@/hooks/use-media-query';
import { type RowProps, useRowKeys } from '@/hooks/use-row-keys';
import { useSort } from '@/hooks/use-sort';
import { clockTime, minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { pausedForViewer } from '@/lib/schedules';
import { cn } from '@/lib/utils';
import { scheduleSortOptions, sortedScheduleGroups } from '@/lib/work-sort';

/**
 * Schedules: the jobs that file themselves (docs/JOB-LIFECYCLE.md 2.7).
 *
 * Under Work, beside Jobs, because a schedule is a job with a clock on it and
 * the jobs it files are ordinary jobs on the Jobs page. What this page adds is
 * the two questions the Jobs page cannot answer: when does this fire next, and
 * what became of the last time it did.
 *
 * The list is split into what is running and what is waiting on a person: a
 * schedule the hub paused after two bad runs is a gate, and putting it in the
 * same list as the healthy ones is how "it just stopped working" happens.
 */
function outcomeTone(outcome: ScheduleSummary['lastOutcome']): string {
  if (outcome === 'failed') return 'text-bad';
  if (outcome === 'skipped') return 'text-warn';
  if (outcome === 'filed') return 'text-ok';
  return 'text-fg-3';
}

function ScheduleRow({
  schedule,
  selected,
  rowProps,
}: {
  schedule: ScheduleSummary;
  selected: boolean;
  rowProps: RowProps;
}) {
  return (
    <li>
      <Link
        to="/schedules/$scheduleId"
        params={{ scheduleId: schedule.id }}
        {...rowProps}
        // Selecting a schedule keeps the sort that found it, same as /tasks.
        search={(previous) => previous}
        className={cn(
          'relative flex items-start gap-3 px-gutter py-3 transition-colors duration-100',
          'hover:bg-raised/60',
          selected && 'bg-sel',
        )}
      >
        {schedule.pausedAt ? (
          <span className="bg-warn absolute inset-y-0 left-0 w-[3px]" aria-hidden />
        ) : null}
        <CalendarClock className="text-fg-3 mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-2.5">
            <TaskKey>{shortKey(schedule.id)}</TaskKey>
            <span className="line-clamp-2 min-w-0 text-base sm:truncate" title={schedule.name}>
              {schedule.name}
            </span>
            {schedule.pausedAt ? (
              <SchedulePausedChip reason={schedule.pauseReason} className="self-center" />
            ) : null}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <RepoChip repo={schedule.repos[0] ?? '-'} extra={schedule.repos.length - 1} />
            <span className="text-fg-3 shrink-0 text-xs">{schedule.cadence}</span>
            <span className="text-fg-3 shrink-0 text-xs">
              {schedule.taskCount} task{schedule.taskCount === 1 ? '' : 's'}
            </span>
          </div>
          <div className="text-fg-3 mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 text-xs">
            <span>
              {schedule.pausedAt ? (
                <span className="text-warn">{schedule.pauseReason ?? 'Paused.'}</span>
              ) : (
                <>Next {clockTime(schedule.nextRunAt)}</>
              )}
            </span>
            {schedule.lastRunAt || schedule.lastOutcome ? (
              <span>
                Last {schedule.lastRunAt ? clockTime(schedule.lastRunAt) : 'never'}
                {schedule.lastOutcome ? (
                  <span className={outcomeTone(schedule.lastOutcome)}>
                    {' · '}
                    {schedule.lastOutcome}
                  </span>
                ) : null}
              </span>
            ) : (
              <span>Has not run yet</span>
            )}
          </div>
        </div>
        <span className="mt-0.5 shrink-0">
          <Age minutes={minutesSince(schedule.updatedAt)} hot={Boolean(schedule.pausedAt)} />
        </span>
      </Link>
    </li>
  );
}

export function SchedulesPage() {
  const params = useParams({ strict: false }) as { scheduleId?: string };
  const navigate = useNavigate();
  const { session } = useSession();
  const schedules = useSchedules();
  const [composing, setComposing] = useState(false);
  const rows = schedules.data ?? [];

  // Three groups, not two. A schedule the hub paused is waiting on the person
  // whose approval it carries and on nobody else: every verb on it answers a
  // stranger with a sentence, so telling every member it is waiting on them is
  // a gate they cannot pass. Somebody else's is still on the page, under a
  // heading that says whose it is.
  const paused = pausedForViewer(rows, session.actor, session.isAdmin);
  const theirs = rows.filter((schedule) => schedule.pausedAt && !paused.includes(schedule));
  const running = rows.filter((schedule) => !schedule.pausedAt);

  const sorting = useSort(scheduleSortOptions(), (schedule) => schedule.id);
  const rawGroups = [
    { key: 'paused', label: 'Waiting on you', rows: paused, empty: 'Nothing is paused.' },
    {
      key: 'running',
      label: 'On the clock',
      rows: running,
      empty: 'No schedules yet. A schedule is a job with a clock on it.',
    },
    { key: 'theirs', label: 'Paused, waiting on somebody else', rows: theirs, empty: '' },
  ].filter((group) => group.rows.length || group.key === 'running');
  const groups = sortedScheduleGroups(rawGroups, sorting.state);

  const wide = useWideLayout();
  const { rowProps } = useRowKeys({
    ids: groups.flatMap((group) => group.rows.map((schedule) => schedule.id)),
    enabled: wide || !params.scheduleId,
  });

  return (
    <>
      <ViewHeader
        title="Schedules"
        icon={CalendarClock}
        tone="var(--dy-plan)"
        sub={`${running.length} on the clock · ${paused.length + theirs.length} paused`}
        actions={
          <>
            <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
            <Button size="pill" variant="hero" onClick={() => setComposing(true)}>
              <Plus aria-hidden />
              New schedule
            </Button>
          </>
        }
      />
      <Page>
        <SplitPane
          list={
            <>
              {composing ? <ScheduleComposer onClose={() => setComposing(false)} /> : null}

              {schedules.isPending && !rows.length ? (
                <Section>
                  <SectionHead title="On the clock" />
                  <Surface>
                    <RowSkeleton rows={3} />
                  </Surface>
                </Section>
              ) : schedules.isError && !schedules.data ? (
                <Section>
                  <SectionHead title="On the clock" />
                  <Surface>
                    <LoadError
                      what="schedules"
                      error={schedules.error}
                      retry={() => void schedules.refetch()}
                    />
                  </Surface>
                </Section>
              ) : (
                groups.map((group) => (
                  <Section key={group.key}>
                    <SectionHead
                      title={group.label}
                      meta={group.rows.length ? String(group.rows.length) : undefined}
                      hue={group.key === 'paused' ? 'var(--dy-warn)' : undefined}
                    />
                    <Surface>
                      {group.rows.length ? (
                        <ul className="divide-line divide-y">
                          {group.rows.map((schedule) => (
                            <ScheduleRow
                              key={schedule.id}
                              schedule={schedule}
                              selected={params.scheduleId === schedule.id}
                              rowProps={rowProps(schedule.id)}
                            />
                          ))}
                        </ul>
                      ) : (
                        <p className="text-fg-3 px-gutter py-6 text-center text-sm">
                          {group.empty}
                        </p>
                      )}
                    </Surface>
                  </Section>
                ))
              )}
            </>
          }
          peekEmpty={!params.scheduleId}
          onClose={() => {
            void navigate({ to: '/schedules' });
          }}
          peek={
            params.scheduleId ? (
              <ScheduleDetail scheduleId={params.scheduleId} />
            ) : (
              <EmptyState
                icon={CalendarClock}
                title="Select a schedule to see its plan"
                hint={
                  <>
                    The tasks it files, when it next fires, and every job it has filed. Creating one
                    approves its plan for every run.
                  </>
                }
              />
            )
          }
        />
      </Page>
    </>
  );
}
